import React, { useState, useEffect } from 'react'
import {
	Box,
	Typography,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	Grid,
	useTheme,
	styled,
} from '@mui/material'
import { loadTopListsFromLocalStorage } from './Settings'

// Styled components for better theme integration
const StyledTableCell = styled(TableCell)(({ theme, variant }) => ({
	padding: '5px 20px',
	fontSize: '40px',
	fontWeight: variant === 'rank' ? '500' : variant === 'name' ? '400' : '500',
	textAlign: variant === 'value' ? 'right' : 'left',
	borderBottom: `2px solid ${theme.palette.divider}`,
}))

const StyledHeaderCell = styled(TableCell)(({ theme, variant }) => ({
	padding: '20px 20px',
	fontSize: '40px',
	fontWeight: '600',
	borderBottom: `4px solid ${theme.palette.divider}`,
	textAlign: variant === 'value' ? 'right' : 'left',
	width: variant === 'rank' ? '20%' : variant === 'name' ? '50%' : '30%',
}))

const StyledTableRow = styled(TableRow)(({ theme, rowtype }) => ({
	borderBottom: `2px solid ${theme.palette.divider}`,
	color:
		rowtype === 'hit'
			? theme.palette.success.main
			: rowtype === 'out'
				? theme.palette.error.main
				: theme.palette.text.primary,
}))

const StyledTable = styled(Table)(({ theme }) => ({
	width: '100%',
	borderCollapse: 'collapse',
}))

function getMaxRankForTopList(topList) {
	if (!topList || !topList.values || !topList.values.length) {
		return topList?.top || 10
	}
	const arr = topList.values
	// Try max of provided ranks; fall back to length if ranks are missing
	const maxFromRanks = arr.reduce((m, it) => {
		const r = parseInt(it.rank, 10)

		return isNaN(r) ? m : Math.max(m, r)
	}, 0)

	return Math.max(maxFromRanks, arr.length, topList.top || 10)
}

const DisplayPage = () => {
	const theme = useTheme()

	const [state, setState] = useState({
		selectedSource: null, // Now stores key instead of number
		selectedItems: [],
		showDescription: true,
		outItems: [], // [{name, source}]
		topLists: [], // Store loaded TopLists
	})

	const loadData = () => {
		const storedShowDescription = JSON.parse(localStorage.getItem('showDescription') || 'false')
		const storedItems = JSON.parse(localStorage.getItem('selectedItems') || '[]')
		const storedKey = localStorage.getItem('selectedSource') || null // Now string
		const storedOutItems = JSON.parse(localStorage.getItem('outItems') || '[]')
		const topLists = loadTopListsFromLocalStorage()

		setState((prev) => ({
			...prev,
			selectedSource: storedKey,
			selectedItems: storedItems,
			outItems: storedOutItems,
			showDescription: storedShowDescription || storedItems.length === 0,
			topLists: topLists,
		}))
	}

	useEffect(() => {
		loadData()
		const refreshInterval = setInterval(loadData, 2000)

		return () => clearInterval(refreshInterval)
	}, [])

	// Group selected items by source (now by key)
	const groupedItems = state.selectedItems.reduce((groups, item) => {
		const sourceKey = item.source
		if (!groups[sourceKey]) {
			groups[sourceKey] = { items: [] }
		}
		groups[sourceKey].items.push(item)

		return groups
	}, {})

	// Sort selected items by rank within each group
	Object.keys(groupedItems).forEach((sourceKey) => {
		groupedItems[sourceKey].items.sort((a, b) => a.rank - b.rank)
	})

	// Get current TopList
	const currentTopList = state.topLists.find((list) => list.key === state.selectedSource)
	const topValue = currentTopList?.top || 10

	const renderCategoryData = (sourceKey) => {
		const baseItems = groupedItems[sourceKey]?.items || []
		const topList = state.topLists.find((list) => list.key === sourceKey)
		const maxRank = getMaxRankForTopList(topList)

		// Build synthetic rows for outItems of this source
		const outForSource = (state.outItems || [])
			.filter((o) => o.source === sourceKey)
			.map((o) => ({
				source: o.source,
				name: o.name,
				// Give a numeric rank just for styling/sorting (beyond top range)
				rank: maxRank + 1,
				// Mark as out item for rendering
				__isOut: true,
			}))

		const items = [...baseItems, ...outForSource]

		const renderTable = (list, keyPrefix) => (
			<StyledTable>
				<TableHead>
					<TableRow>
						<StyledHeaderCell variant="rank">Rang</StyledHeaderCell>
						<StyledHeaderCell variant="name">Name</StyledHeaderCell>
						<StyledHeaderCell variant="value">Wert</StyledHeaderCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{list.map((item) => {
						const itemRank = parseInt(item.rank, 10)
						const rowType =
							topValue > itemRank ? 'normal' : topValue === itemRank ? 'hit' : 'out'

						return (
							<StyledTableRow
								key={`${keyPrefix}-${item.source}-${item.name}`}
								rowtype={rowType}
							>
								<StyledTableCell variant="rank">
									{item.__isOut ? `>${maxRank}` : `#${item.rank}`}
								</StyledTableCell>
								<StyledTableCell variant="name">{item.name}</StyledTableCell>
								<StyledTableCell variant="value">
									{item.value !== undefined && item.value !== ''
										? typeof item.value === 'number'
											? item.value.toLocaleString('de-CH')
											: item.value
										: ''}
								</StyledTableCell>
							</StyledTableRow>
						)
					})}
				</TableBody>
			</StyledTable>
		)

		if (items.length <= 10) {
			return renderTable(items, 'single')
		}

		const mid = Math.ceil(items.length / 2)
		const first = items.slice(0, mid)
		const second = items.slice(mid)

		return (
			<Grid container spacing={5}>
				<Grid item xs={12} md={6}>
					{renderTable(first, 'col1')}
				</Grid>
				<Grid item xs={12} md={6}>
					{renderTable(second, 'col2')}
				</Grid>
			</Grid>
		)
	}

	const renderDescription = () => {
		if (!currentTopList) return null

		return (
			<Box key={state.selectedSource} sx={{ marginBottom: '40px' }}>
				<Typography
					variant="h2"
					component="h2"
					sx={{
						fontSize: '40px',
						fontWeight: '600',
						padding: '20px 0',
						borderBottom: `4px solid ${theme.palette.divider}`,
						marginBottom: '20px',
						marginTop: '0px',
					}}
				>
					Top {currentTopList.top}: {currentTopList.name}
				</Typography>
				{(currentTopList.descriptions || []).map((desc, index) => (
					<Typography
						key={`${desc}-${index}`}
						variant="h3"
						component="h3"
						sx={{
							padding: '5px 20px',
							fontSize: '40px',
							fontWeight: '400',
						}}
					>
						{desc}
					</Typography>
				))}
			</Box>
		)
	}

	// If no source is selected or TopList doesn't exist, show nothing
	if (!state.selectedSource || !currentTopList) {
		return <Box />
	}

	const hasAnyForSource =
		groupedItems.hasOwnProperty(state.selectedSource) ||
		(state.outItems || []).some((o) => o.source === state.selectedSource)

	return (
		<Box
			sx={{
				width: '100%',
				padding: '20px',
				fontFamily: 'Arial, sans-serif',
				backgroundColor: theme.palette.background.default,
				color: theme.palette.text.primary,
				minHeight: '100vh',
			}}
		>
			{state.showDescription || !hasAnyForSource ? (
				renderDescription()
			) : (
				<Box sx={{ marginBottom: '40px' }}>
					<Typography
						variant="h2"
						component="h2"
						sx={{
							fontSize: '40px',
							fontWeight: '600',
							padding: '20px 0',
							borderBottom: `4px solid ${theme.palette.divider}`,
							marginBottom: '20px',
							marginTop: '0px',
						}}
					>
						{currentTopList.name}
					</Typography>
					{renderCategoryData(state.selectedSource)}
				</Box>
			)}
		</Box>
	)
}

export default DisplayPage
